import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Services', href: '/services' },
  { label: 'Gallery', href: '/gallery' },
  { label: 'About', href: '/about' },
  {
    label: 'Locations',
    href: '#',
    children: [
      { label: 'Tampa', href: '/locations/tampa' },
      { label: 'St. Petersburg', href: '/locations/st-petersburg' },
      { label: 'Clearwater', href: '/locations/clearwater' },
      { label: 'Tarpon Springs', href: '/locations/tarpon-springs' },
    ],
  },
  { label: 'Contact', href: '/contact' },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location]);

  return (
    <>
      {/* Announcement Bar */}
      <div className="bg-parkside-green text-white text-xs font-semibold text-center py-2.5 px-4 z-50 relative">
        <span className="hidden sm:inline">
          <i className="ri-time-line mr-1.5" />
          Limited Spring 2026 Availability — Schedule Your Free Estimate Today
        </span>
        <span className="sm:hidden">Limited Spring Availability —&nbsp;</span>
        <a href="tel:813-404-6313" className="underline underline-offset-2 hover:text-parkside-gold transition-colors cursor-pointer whitespace-nowrap">
          Call (813) 404-6313
        </a>
      </div>

      <nav className="sticky top-0 left-0 right-0 z-40 bg-parkside-darker border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0 group">
            <div className="flex items-center gap-2.5 filter brightness-0 invert">
              <img
                src="https://www.parksidepavers.com/files/user/logo.svg"
                alt="Parkside Pavers - Tampa Bay Paver Installation"
                className="h-12 w-auto"
              />
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link.children ? (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => setActiveDropdown(link.label)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <button
                    className="flex items-center gap-1 text-sm font-semibold px-4 py-2 rounded-md transition-colors cursor-pointer whitespace-nowrap text-white/85 hover:text-white hover:bg-white/10"
                  >
                    {link.label}
                    <span className="w-4 h-4 inline-flex items-center justify-center">
                      <i className="ri-arrow-down-s-line text-xs" />
                    </span>
                  </button>
                  {activeDropdown === link.label && (
                    <div className="absolute top-full left-0 mt-1 bg-white rounded-xl border border-stone-100 py-2 w-48 shadow-card-hover">
                      {link.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className="flex items-center gap-2 px-4 py-2.5 text-sm text-parkside-dark hover:text-parkside-green hover:bg-parkside-cream transition-colors cursor-pointer"
                        >
                          <span className="w-4 h-4 inline-flex items-center justify-center">
                            <i className="ri-map-pin-line text-parkside-amber text-xs" />
                          </span>
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-sm font-semibold px-4 py-2 rounded-md transition-colors cursor-pointer whitespace-nowrap text-white/85 hover:text-white hover:bg-white/10"
                >
                  {link.label}
                </Link>
              )
            )}
          </div>

          {/* Desktop Right CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:813-404-6313"
              className="flex items-center gap-2 text-sm font-bold transition-colors whitespace-nowrap cursor-pointer px-3 py-2 rounded-md text-white hover:bg-white/10"
            >
              <span className="w-5 h-5 inline-flex items-center justify-center">
                <i className="ri-phone-fill text-sm" />
              </span>
              (813) 404-6313
            </a>
            <Link
              to="/contact"
              className="bg-parkside-amber text-white text-sm font-bold px-5 py-2.5 rounded-lg hover:bg-parkside-amber/90 transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5"
            >
              Free Estimate
              <span className="w-4 h-4 inline-flex items-center justify-center">
                <i className="ri-arrow-right-line text-xs" />
              </span>
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer text-white hover:bg-white/10"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle navigation"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`} />
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-parkside-darker border-t border-white/10 px-6 py-5 flex flex-col gap-1">
            {navLinks.map((link) =>
              link.children ? (
                <div key={link.label}>
                  <p className="text-xs font-bold uppercase tracking-widest text-parkside-amber mt-3 mb-2 px-3">
                    {link.label}
                  </p>
                  {link.children.map((child) => (
                    <Link
                      key={child.href}
                      to={child.href}
                      className="flex items-center gap-2.5 px-3 py-2.5 text-white/80 font-medium text-sm rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
                    >
                      <span className="w-4 h-4 inline-flex items-center justify-center">
                        <i className="ri-map-pin-line text-parkside-amber text-xs" />
                      </span>
                      {child.label}
                    </Link>
                  ))}
                </div>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className="px-3 py-2.5 text-white/80 font-semibold text-sm rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
                >
                  {link.label}
                </Link>
              )
            )}
            <div className="mt-4 flex flex-col gap-3 pt-4 border-t border-white/10">
              <a
                href="tel:813-404-6313"
                className="flex items-center justify-center gap-2 py-3 border border-white/20 rounded-lg text-white font-bold text-sm cursor-pointer hover:bg-white/10"
              >
                <i className="ri-phone-fill" />
                (813) 404-6313
              </a>
              <Link
                to="/contact"
                className="bg-parkside-amber text-white font-bold text-sm py-3 rounded-lg text-center cursor-pointer hover:bg-parkside-amber/90 transition-all"
              >
                Get a Free Estimate
              </Link>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
